import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useState } from "react";
import type { MorphemZerlegungExerciseConfig, MorphemZerlegungRunSettings } from "@calega/content";
import {
  classifyMorphemZerlegungAnswer,
  morphemSegmentsFromCuts,
  type MorphemZerlegungRun,
} from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Phase = "running" | "feedback" | "complete";
interface Answer { itemId: string; correct: boolean }
interface State {
  result?: { score: number; maxScore: number };
  runId: string;
  run: MorphemZerlegungRun;
  index: number;
  phase: Phase;
  endsAt: number | null;
  cuts: number[];
  answers: Answer[];
}

export function MorphemZerlegung({ account, config, pageId }: { account: Account; config: MorphemZerlegungExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const syncFollower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canAnswer = !realtime.session || facilitator || syncFollower;
  const [mode, setMode] = useState<"count" | "time">(config.defaultMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [exercise, setExercise] = useState<State | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [, setClockTick] = useState(0);
  const score = useMemo(() => (exercise?.answers ?? []).filter(({ correct }) => correct).length, [exercise?.answers]);

  function publish(state: State | null) {
    realtime.publishExercise({ exerciseType: "morphem-zerlegung", pageId, state });
  }

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: State | null } | null;
    if (synced?.exerciseType === "morphem-zerlegung" && synced.pageId === pageId) setExercise(synced.state ?? null);
  }, [realtime.state.exercise, pageId]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const interval = window.setInterval(() => setClockTick((value) => value + 1), 250);
    return () => window.clearInterval(interval);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const timer = setExerciseTimeout(
      () => finish().catch((error) => setMessage(error.message)),
      Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow)) + 20,
    );
    return () => clearExerciseTimeout(timer);
  }, [exercise?.endsAt, exercise?.phase, exercise?.index, exercise?.answers.length, canControl, busy]);

  function currentSettings(): MorphemZerlegungRunSettings {
    return { mode, count, durationSeconds };
  }

  async function start(settings = currentSettings()) {
    if (busy || !canControl) return;
    setBusy(true);
    setMessage("Durchlauf wird vorbereitet …");
    try {
      const result = await startExerciseRun<{ runId: string; exercise: MorphemZerlegungRun }>({ contentPageId: pageId, settings });
      const next: State = {
        runId: result.runId,
        run: result.exercise,
        index: 0,
        phase: "running",
        endsAt: settings.mode === "time" ? exerciseNow(realtime.serverNow) + settings.durationSeconds * 1_000 : null,
        cuts: [],
        answers: [],
      };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function abortCurrent() {
    if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId);
  }

  async function backToSettings() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      await abortCurrent();
      setMode(exercise.run.settings.mode);
      setCount(exercise.run.settings.count);
      setDurationSeconds(exercise.run.settings.durationSeconds);
      setExercise(null);
      setMessage("");
      publish(null);
    } finally {
      setBusy(false);
    }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const settings = exercise.run.settings;
    setBusy(true);
    try {
      await abortCurrent();
    } finally {
      setBusy(false);
    }
    await start(settings);
  }

  function selectCut(position: number) {
    if (!exercise || exercise.phase !== "running" || !canAnswer) return;
    const current = exercise.cuts;
    const cuts = current.includes(position)
      ? current.filter((cut) => cut !== position)
      : current.length < 2
        ? [...current, position].sort((first, second) => first - second)
        : current.map((cut, index) => index === (Math.abs(current[0]! - position) <= Math.abs(current[1]! - position) ? 0 : 1) ? position : cut)
          .sort((first, second) => first - second);
    const next = { ...exercise, cuts };
    setExercise(next);
    publish(next);
  }

  async function check() {
    if (!exercise || exercise.phase !== "running" || exercise.cuts.length !== 2 || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index];
    if (!item) return;
    const cuts = exercise.cuts as [number, number];
    const result = classifyMorphemZerlegungAnswer(item, cuts);
    if (!result) return;
    setBusy(true);
    try {
      await submitExerciseAnswer(exercise.runId, { type: "morphem-zerlegung", itemId: item.id, cuts });
      const next: State = {
        ...exercise,
        phase: "feedback",
        answers: [...exercise.answers, { itemId: item.id, correct: result.correct }],
      };
      setExercise(next);
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function next() {
    if (!exercise || exercise.phase !== "feedback" || busy || !canControl) return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length) return finish();
    const nextState: State = { ...exercise, index: exercise.index + 1, phase: "running", cuts: [] };
    setExercise(nextState);
    publish(nextState);
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const nextState = { ...exercise, result, phase: "complete" as const };
      setExercise(nextState);
      publish(nextState);
      setMessage("");
    });
  }
  async function closeCompletedRun(repeat: boolean) { if (!exercise || exercise.phase !== "complete") return; if (repeat) await remix(); else await backToSettings(); }

  if (syncFollower && !exercise) return <ExerciseWaitingCard className="spelling-choice-exercise morphem-zerlegung-exercise" />;
  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (!exercise) return <section className="spelling-choice-exercise morphem-zerlegung-exercise">
    <p className="quiz-instructions learning-text">{config.instructions}</p>
    <div className="spelling-choice-settings">
      <fieldset>
        <legend>Durchlauf</legend>
        <div className="blitz-choice-row">
          <label className="checkbox-label"><input type="radio" name={`${pageId}-morphem-zerlegung-modus`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label>
          <label className="checkbox-label"><input type="radio" name={`${pageId}-morphem-zerlegung-modus`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label>
        </div>
      </fieldset>
      {mode === "count"
        ? <label>Aufgabenanzahl<select value={count} onChange={(event) => setCount(Number(event.target.value))}>{[5, 10, 15, 20, 30, 40].filter((value) => value <= config.items.length).map((value) => <option key={value}>{value}</option>)}</select></label>
        : <ExerciseDurationSelect value={durationSeconds} onChange={setDurationSeconds} />}
    </div>
    <button className="primary" disabled={busy || !canControl} onClick={() => start().catch((error) => setMessage(error.message))}>Wortbausteine starten</button>
    {message && <div className="alert info">{message}</div>}
  </section>;

  const item = exercise.run.items[exercise.index];
  const selectedSegments = item && exercise.cuts.length === 2
    ? morphemSegmentsFromCuts(item.word, exercise.cuts as [number, number])
    : null;
  const result = item && exercise.cuts.length === 2
    ? classifyMorphemZerlegungAnswer(item, exercise.cuts as [number, number])
    : null;
  const timeRemaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  return <section className="spelling-choice-exercise morphem-zerlegung-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu>
      <button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button>
      <button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button>
    </ExerciseMenu>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${exercise.answers.length} beantwortet · ${timeRemaining ?? 0} s` : `Aufgabe ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`}</div>
    {exercise.phase === "complete"
        ? <div className="reime-result"><strong>{exercise.result?.score ?? score} von {exercise.result?.maxScore ?? exercise.answers.length} richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div>
      : item && <div className="morphem-segmentation-card">
          <p>Setze genau zwei Trennstellen.</p>
          <div className="morphem-cut-word" aria-label={`Trennstellen im Wort ${item.word}`}>
            {Array.from(item.word).map((character, index) => <span className="morphem-cut-part learning-text" key={`${character}-${index}`}>
              {index === 0 && <button type="button" aria-label="Trennstelle vor dem Wort" className={exercise.cuts.includes(0) ? "is-selected" : ""} disabled={exercise.phase !== "running" || busy || !canAnswer} onClick={() => selectCut(0)} />}
              <span>{character}</span>
              <button type="button" aria-label={`Trennstelle nach Zeichen ${index + 1}`} className={exercise.cuts.includes(index + 1) ? "is-selected" : ""} disabled={exercise.phase !== "running" || busy || !canAnswer} onClick={() => selectCut(index + 1)} />
            </span>)}
          </div>
          <div className="morphem-segment-preview learning-text">
            <div><span>Präfix</span><strong>{selectedSegments?.prefix || "–"}</strong></div>
            <div><span>Stamm</span><strong>{selectedSegments?.stem || "–"}</strong></div>
            <div><span>Suffix</span><strong>{selectedSegments?.suffix || "–"}</strong></div>
          </div>
          {exercise.phase === "feedback" && result && <div className={`answer-feedback learning-text ${result.correct ? "correct" : "wrong"}`}>
            {result.correct
              ? <>Richtig: <strong>{item.prefix || "–"} | {item.stem} | {item.suffix || "–"}</strong></>
              : <>Deine Zerlegung: <strong>{result.selectedSegments.prefix || "–"} | {result.selectedSegments.stem || "–"} | {result.selectedSegments.suffix || "–"}</strong><br />Richtig ist: <strong>{item.prefix || "–"} | {item.stem} | {item.suffix || "–"}</strong></>}
          </div>}
          {exercise.phase === "running" && <button className="primary" disabled={busy || exercise.cuts.length !== 2} onClick={() => check().catch((error) => setMessage(error.message))}>Überprüfen</button>}
          {exercise.phase === "feedback" && canControl && <button className="primary" disabled={busy} onClick={() => next().catch((error) => setMessage(error.message))}>{(exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length ? "Auswertung ansehen" : "Nächstes Wort"}</button>}
        </div>}
    {canControl && exercise.phase === "complete" && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}
    {exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
